import { useMemo } from 'react';
import type { RouteName } from '@atlassian/jira-route-name-types/src/RouteName.tsx';
import { type Query, type Match, useRouter } from '@atlassian/react-resource-router';

import { useJiraUserBoardNavigationResource } from '../common/jsw-user-board-navigation-resource/index.tsx';
import { useJiraSoftwareProjectNavigation } from '../common/project-sidebar-navigation-resource/index.tsx';
import type { UseBoardReturn } from './types.tsx';
import { extractData, extractUserBoard } from './utils/extract-data.tsx';

const USER_BOARD_ROUTE_NAMES: RouteName[] = [
	'rapidboard-user-board',
	'rapidboard-user-backlog',
	'rapidboard-user-report',
	'rapidboard-user-board-settings',
	'rapidboard-user-reports-overview',
	'software-reports-velocity-user-board',
];

const isOnUserBoardRoute = (routeName: RouteName): boolean =>
	USER_BOARD_ROUTE_NAMES.includes(routeName);

const isOnLocationLessBoardRoute = (match: Match, query: Query): boolean => {
	const { locationless = 'false' } = query;
	return match.url.includes('RapidBoard.jspa') && JSON.parse(locationless) === true;
};

export const useIsOnUserBoardOrLocationLessBoardRoute = (): boolean => {
	const [{ match, route, query }] = useRouter();
	return isOnUserBoardRoute(route.name) || isOnLocationLessBoardRoute(match, query);
};

export const useBoard = (): UseBoardReturn => {
	const isOnUserOrLocationlessBoardRoute = useIsOnUserBoardOrLocationLessBoardRoute();
	// pb -> project board
	const { data: pb, loading: pbLoading, error: pbError } = useJiraSoftwareProjectNavigation();
	// ub -> user board
	const { data: ub, loading: ubLoading, error: ubError } = useJiraUserBoardNavigationResource();

	const projectBoard = useMemo(() => pb && extractData(pb), [pb]);
	const userBoard = useMemo(() => ub && extractUserBoard(ub), [ub]);

	if (isOnUserOrLocationlessBoardRoute) {
		return { data: userBoard, loading: ubLoading, error: ubError };
	}
	return { data: projectBoard, loading: pbLoading, error: pbError };
};
